/**
 * fieldStore.js — write/read API over fieldDb (Inventory Screens Step 4.2a).
 *
 * Pure persistence layer: no fetching, no auth, no routing. The Load
 * orchestrator (4.2b+) fetches get_field_sites / get_field_records and hands
 * the raw responses here; the capture UI (Step 5) reads through the getters.
 *
 * Record row shape (mirrors the Load projection exactly — see
 * get_field_records in function_app.py):
 *   {
 *     record_id,            // Cosmos doc id (rec.id) — pk; tool_id may repeat
 *     tool_id, site_id, etag,
 *     baseline: {…scalars…},// immutable Load scalar projection (incl. the
 *                           //   v14 null-defaulted keys + legacy_location)
 *     working:  {…},        // deep clone of baseline — the edit surface (4.3)
 *     dirty:    {},         // SENTINEL SET, values never read: a scalar
 *                           //   field-name key present = that scalar is
 *                           //   edited; part_numbers/asset_tags === true =
 *                           //   that child group is edited
 *     part_numbers:       [{id, value, type}],   // Load slots, verbatim
 *     extra_part_numbers: [{id, value, type}],   // overflow (slot 11+)
 *     asset_tags:         [{id, value}],
 *     extra_asset_tags:   [{id, value}],
 *     media: [{media_id, photo_type, blob_path, content_hash, uploaded_at, sas_url}],
 *     cached_at,
 *     removed_part_number_ids?: [id],  // PN-STALE B2: server ids the field ×
 *     removed_asset_tag_ids?:   [id],  //   removed; absent until the first
 *                                      //   removal, readers treat absent as []
 *   }
 */

import { fieldDb } from './fieldDb';
import { SERVER_OWNED, CHILD_SENTINELS, REMOVAL_LIST_KEYS } from '../sync/buildCapturePayload';
import { isPendingRecord } from '../sync/pendingId';

const SNAPSHOT_KEY = 'current';

// Dexie does NOT auto-reopen an instance after delete()/close() (verified
// against Dexie 4.4.4 — first post-purge op rejects with DatabaseClosedError
// without this). Every op below goes through this guard so purge → next op
// transparently re-creates the empty DB. Exported for the sync layer
// (fieldOutbox.js) so there is exactly ONE guard, never a drifting copy.
export async function ensureOpen() {
  if (!fieldDb.isOpen()) await fieldDb.open();
}

// Child arrays are stored as first-class row keys; everything else on the
// Load record (minus etag) is the scalar baseline.
const CHILD_ARRAY_KEYS = new Set([
  'part_numbers', 'extra_part_numbers', 'asset_tags', 'extra_asset_tags', 'media',
]);

/**
 * Scalar baseline from one field-shape record: every key except the hoisted
 * etag and the child arrays. Extracted from toStoredRecord (behavior
 * identical) so the Path-2 re-base builds its fresh baseline through the
 * SAME exclusion set — one definition, never a drifting copy.
 */
export function buildBaseline(rec) {
  const baseline = {};
  for (const [k, v] of Object.entries(rec)) {
    if (k !== 'etag' && !CHILD_ARRAY_KEYS.has(k)) baseline[k] = v;
  }
  return baseline;
}

function toStoredRecord(rec, cachedAt) {
  const baseline = buildBaseline(rec);
  return {
    record_id: rec.id,
    tool_id: rec.tool_id,
    site_id: rec.site_id,
    etag: rec.etag,
    baseline,
    working: structuredClone(baseline),
    dirty: {},
    part_numbers: rec.part_numbers || [],
    extra_part_numbers: rec.extra_part_numbers || [],
    asset_tags: rec.asset_tags || [],
    extra_asset_tags: rec.extra_asset_tags || [],
    media: rec.media || [],
    cached_at: cachedAt,
  };
}

/**
 * Re-Load merge for one incoming server record against an existing local row
 * (4.3c-ii). Locked rule:
 *   - CLEAN existing (no dirty keys) → wholesale refresh (fresh toStoredRecord).
 *   - DIRTY existing → start fresh, then overlay the pending work:
 *       · each dirty scalar (not a child sentinel, not SERVER_OWNED) keeps
 *         BOTH existing.baseline[k] (the conflict basis — consistent with the
 *         kept etag) and existing.working[k] (the pending edit). This also
 *         carries a dirty scalar the server row no longer has (never-drop-work).
 *       · etag stays the existing Load basis — a dirty row must not silently
 *         re-base its If-Match onto server state the user never saw.
 *       · dirty is copied through untouched (nothing synced, nothing clears).
 *       · a dirty child group (sentinel === true) keeps its existing arrays
 *         (incl. id-less local adds); a clean group refreshes from the server.
 *       · media is ALWAYS fresh — no dirty sentinel exists for media; offline
 *         media adds ride 4.3d's media outbox, not this merge.
 *       · last_dropped is preserved (a clean row drops it via the refresh).
 *       · the PN-STALE B2 removal lists (REMOVAL_LIST_KEYS) are preserved
 *         whenever present — NOT gated on dirty[group] (spec v1.2 §5.5: gating
 *         couples two things that can drift; a re-sent removal is a no-op).
 *         Like last_dropped, a clean row drops them via the refresh.
 * `existing` comes off bulkGet (structured-clone deserialization), so its
 * baseline/working values are already independent objects — the overlay
 * introduces no aliasing.
 */
function mergeRecord(existing, rec, cachedAt) {
  // 5.3 Phase 2 follow-up: media is ALWAYS fresh below, so every optimistic
  // media entry on the existing row is superseded right here — this is one
  // of the two lifecycle exits where its object-URL preview gets revoked
  // (the other is delete in the modal). Reconcile's stamp deliberately does
  // NOT revoke — a stale prop copy may still render the entry (the
  // backlogged modal-liveness gap).
  for (const m of existing.media || []) {
    if (m && m.object_url) {
      try { URL.revokeObjectURL(m.object_url); } catch (revokeErr) { /* non-fatal */ }
    }
  }
  const fresh = toStoredRecord(rec, cachedAt);
  const dirty = existing.dirty || {};
  const dirtyKeys = Object.keys(dirty);
  if (dirtyKeys.length === 0) return fresh;

  const merged = fresh;
  for (const k of dirtyKeys) {
    if (CHILD_SENTINELS.has(k) || SERVER_OWNED.has(k)) continue;
    merged.baseline[k] = existing.baseline[k];
    merged.working[k] = existing.working[k];
  }
  merged.etag = existing.etag;
  merged.dirty = { ...dirty };
  if (dirty.part_numbers === true) {
    merged.part_numbers = existing.part_numbers;
    merged.extra_part_numbers = existing.extra_part_numbers;
  }
  if (dirty.asset_tags === true) {
    merged.asset_tags = existing.asset_tags;
    merged.extra_asset_tags = existing.extra_asset_tags;
  }
  if ('last_dropped' in existing) {
    merged.last_dropped = existing.last_dropped;
  }
  // Same-row race fix (2026-10-28, C-4): a held capture request survives a
  // re-Load like the dirt it belongs to. A held row is always dirty, so the
  // clean early return above never drops one.
  if (existing.capture_held) {
    merged.capture_held = true;
  }
  for (const listKey of Object.values(REMOVAL_LIST_KEYS)) {
    if (listKey in existing) merged[listKey] = existing[listKey];
  }
  return merged;
}

/**
 * Persist a full Load into the field DB: one snapshot row, the site cards,
 * and the record set (baseline = Load projection, working = clone, dirty = {}).
 *
 * @param {Object} params
 * @param {Object} params.sitesResp   — get_field_sites response { identity, sites }
 * @param {Object|Object[]} params.recordsResp — get_field_records response
 *   { site, records, continuation_token, has_more }, or an array of page
 *   responses (multi-page Loads concatenate in request order)
 * @param {Object} [params.identity]  — overrides sitesResp.identity when given
 * @param {Object} [params.profile]   — the client userProfile (get_user_profile shape)
 * @returns {Promise<{sites: number, records: number, skipped: number}>}
 *
 * Re-Load merge (4.3c-ii): absent or CLEAN local rows get a wholesale refresh
 * (toStoredRecord — last_dropped naturally drops). DIRTY rows merge via
 * mergeRecord: pending work survives (working + dirty + the Load-basis etag +
 * dirty child groups incl. id-less local adds) while everything clean
 * refreshes from the server (clean scalars in baseline AND working, clean
 * child groups, media always).
 */
export async function persistLoadResponse({ sitesResp, recordsResp, identity, profile }) {
  try {
    await ensureOpen();
    const cachedAt = new Date().toISOString();
    const sites = (sitesResp && sitesResp.sites) || [];
    const pages = Array.isArray(recordsResp) ? recordsResp : [recordsResp];
    const rawRecords = pages.flatMap((p) => (p && p.records) || []);

    const validRecs = [];
    let skipped = 0;
    for (const rec of rawRecords) {
      if (!rec || !rec.id) {
        // A Load record without a Cosmos id has no stable pk — surface it.
        console.warn('fieldStore.persistLoadResponse: record missing id, skipped', rec);
        skipped += 1;
        continue;
      }
      validRecs.push(rec);
    }

    const snapshotRow = {
      id: SNAPSHOT_KEY,
      identity: identity ?? (sitesResp && sitesResp.identity) ?? null,
      profile: profile ?? null,
      cached_at: cachedAt,
    };

    await fieldDb.transaction('rw', fieldDb.snapshot, fieldDb.sites, fieldDb.records, async () => {
      await fieldDb.snapshot.put(snapshotRow);
      await fieldDb.sites.bulkPut(sites);
      // Read-merge-write inside ONE txn: the bulkGet and bulkPut must see a
      // consistent records store (no interleaved writer between them).
      const ids = validRecs.map((r) => r.id);
      const existingRows = await fieldDb.records.bulkGet(ids); // id-aligned; undefined where absent
      const merged = validRecs.map((rec, i) =>
        existingRows[i] ? mergeRecord(existingRows[i], rec, cachedAt) : toStoredRecord(rec, cachedAt)
      );
      await fieldDb.records.bulkPut(merged);
    });

    return { sites: sites.length, records: validRecs.length, skipped };
  } catch (err) {
    console.error('fieldStore.persistLoadResponse failed:', err);
    throw err;
  }
}

/**
 * Commit one field edit to a record's WORKING set and mark it dirty
 * (5.2 Phase 2 — the field zone's first dirt writer, D-5.2-F).
 *
 * Writes working[key] = value and sets the dirty scalar sentinel
 * (key-presence = edited; the value is never read — 4.3c-ii contract).
 * NEVER touches baseline (the conflict basis) or etag (D-4.U: a dirty row's
 * etag is never re-based client-side). Server-owned and child-sentinel keys
 * are rejected outright — belt-and-suspenders under the D-5.2-E manifest.
 *
 * P2-5 Phase 1 (B-218): a PENDING row's TOOL ID lives in two places, the row
 * and its queued tool_create (fieldOutbox ENQUEUE_FIELDS), and the drain posts
 * the queue item's copy. So a supplier_tool_id edit on a pending- row also
 * rewrites every tool_create for that record the server has not yet saved, in
 * the same transaction — the move retryDuplicateToolCreate makes, without its
 * status flip. A create already 'saved' is left alone: the server has its ID,
 * and the row's dirty flag carries the edit through the re-key and the
 * follow-up capture (supplier_tool_id left CREATE_STAMPED_KEYS for this). A
 * blank value is not written into the queue item; whether TOOL ID may be
 * cleared on edit is the edit UI's to decide.
 *
 * @returns {Promise<Object>} the updated record row (for host state refresh)
 */
export async function updateWorkingField(recordId, key, value) {
  if (SERVER_OWNED.has(key) || CHILD_SENTINELS.has(key)) {
    throw new Error(`updateWorkingField: "${key}" is not client-writable`);
  }
  const rewriteQueuedCreate = key === 'supplier_tool_id' && isPendingRecord(recordId)
    && typeof value === 'string' && value.trim() !== '';
  try {
    await ensureOpen();
    let updated = null;
    const tables = rewriteQueuedCreate ? [fieldDb.records, fieldDb.syncQueue] : [fieldDb.records];
    await fieldDb.transaction('rw', tables, async () => {
      const record = await fieldDb.records.get(recordId);
      if (!record) throw new Error(`updateWorkingField: record ${recordId} not found`);
      record.working = { ...(record.working || {}), [key]: value };
      record.dirty = { ...(record.dirty || {}), [key]: true };
      await fieldDb.records.put(record);
      if (rewriteQueuedCreate) {
        await fieldDb.syncQueue.where('record_id').equals(recordId)
          .filter((i) => i.type === 'tool_create' && i.status !== 'saved')
          .modify({ supplier_tool_id: value });
      }
      updated = record;
    });
    return updated;
  } catch (err) {
    console.error('fieldStore.updateWorkingField failed:', err);
    throw err;
  }
}

/**
 * Replace a record's child set and mark its group dirty (5.2 Phase 3 —
 * D-5.2-B). `group` is 'part_numbers' | 'asset_tags'. `entries` is the FULL
 * merged working set (the caller merges primary + extra_* first); it lands
 * in the primary array and the extra_* overflow empties — the split is
 * Load-slotting legacy and the capture contract is merged-complete-set, so
 * flattening loses nothing (buildCapturePayload, reconcile, and mergeRecord
 * all operate across both arrays).
 *
 * Sets dirty[group] = true (SENTINEL, boolean true, value never read —
 * 4.3c-ii). baseline and etag untouched (D-4.U).
 *
 * PN-STALE B2 (spec v1.2 §5.2, §6.2) — records removals. Every server id in the
 * STORED set, record[group] ∪ record[`extra_${group}`], that is absent from
 * `entries` is appended to the row's removal list (REMOVAL_LIST_KEYS[group]);
 * buildCapturePayload sends it beside the group array and the server DELETES
 * those children. Entries without an id (a local add: `local_key` only) never
 * reached the server and are never listed. Deduplicated; the key is created by
 * the first removal and absent before it. The diff runs over the union because
 * a Load parks slots 11+ in extra_* (function_app.py:10992-11003): a diff over
 * record[group] alone would miss every overflow removal.
 *
 * ⚠️ THE CALLER CONTRACT IS LOAD-BEARING. `entries` MUST be the full merged set.
 * A caller that passes a PARTIAL array — the visible ten, a filtered view, one
 * page — gets every child it left out listed, and DELETED SERVER-SIDE on the
 * next sync. The only caller today is FieldRecordModal's commitChildren, whose
 * six call sites all build from mergedChildren;
 * FieldRecordModal.commitChildrenMerged.test.js pins that at every call site.
 *
 * @returns {Promise<Object>} the updated record row
 */
export async function updateChildSet(recordId, group, entries) {
  if (!CHILD_SENTINELS.has(group)) {
    throw new Error(`updateChildSet: "${group}" is not a child group`);
  }
  try {
    await ensureOpen();
    let updated = null;
    await fieldDb.transaction('rw', fieldDb.records, async () => {
      const record = await fieldDb.records.get(recordId);
      if (!record) throw new Error(`updateChildSet: record ${recordId} not found`);
      const listKey = REMOVAL_LIST_KEYS[group];
      const kept = new Set(entries.map((e) => e && e.id).filter(Boolean));
      const removed = [...(record[group] || []), ...(record[`extra_${group}`] || [])]
        .map((e) => e && e.id)
        .filter((id) => id && !kept.has(id));
      if (removed.length > 0) {
        record[listKey] = [...new Set([...(record[listKey] || []), ...removed])];
      }
      record[group] = entries;
      record[`extra_${group}`] = [];
      record.dirty = { ...(record.dirty || {}), [group]: true };
      await fieldDb.records.put(record);
      updated = record;
    });
    return updated;
  } catch (err) {
    console.error('fieldStore.updateChildSet failed:', err);
    throw err;
  }
}

/** The 'current' snapshot row ({ identity, profile, cached_at }) or undefined. */
export async function getFieldSnapshot() {
  try {
    await ensureOpen();
    return await fieldDb.snapshot.get(SNAPSHOT_KEY);
  } catch (err) {
    console.error('fieldStore.getFieldSnapshot failed:', err);
    throw err;
  }
}

/**
 * PURE merge rule for the refdata row (the v2 `refdata` store — see fieldDb.js).
 *
 * A requested field's list is replaced only when the response carries a
 * NON-EMPTY array for it. [], a missing key or a non-array keeps the cached
 * list: get_reference_data answers a missing ReferenceData document with [],
 * so [] means "nothing" here. Accepted cost: a vocabulary emptied to zero
 * server-side never reaches a device; values removed WITHIN a list still do.
 *
 * Returns { options, fields, replaced }. `fields` = the sorted keys this row
 * holds a list for; `replaced` = the requested fields that took a new list.
 */
export function mergeRefDataOptions(baseOptions, fetched, requestedFields) {
  const options = { ...(baseOptions || {}) };
  const replaced = [];
  for (const f of requestedFields) {
    const list = fetched ? fetched[f] : undefined;
    if (Array.isArray(list) && list.length > 0) {
      options[f] = list;
      replaced.push(f);
    }
  }
  const fields = Object.keys(options).filter((k) => Array.isArray(options[k])).sort();
  return { options, fields, replaced };
}

/**
 * The cached row under `key` as { options, fields }, or null when nothing is
 * cached. `fields` is the row's own record of which lists it holds — the read
 * hook compares it with the fields it wants and fetches when the row is
 * short (OEM arc Phase 2). A row with no usable `fields` gets them derived
 * from its options by mergeRefDataOptions' rule; every writer here stores
 * them, so that arm is defensive only.
 *
 * ONE-TIME LEGACY MIGRATION on a miss: a row under `legacyKey` is copied
 * under `key` (its fetched_at kept, `fields` taken from its lists) and served.
 * The legacy row is LEFT IN PLACE — leaving it is harmless, deleting it adds
 * risk. Without this, the key change would itself orphan every device's row.
 */
export async function readRefData(key, legacyKey) {
  try {
    await ensureOpen();
    return await fieldDb.transaction('rw', fieldDb.refdata, async () => {
      const row = await fieldDb.refdata.get(key);
      if (row && row.options) {
        const fields = Array.isArray(row.fields)
          ? row.fields
          : mergeRefDataOptions(row.options, null, []).fields;
        return { options: row.options, fields };
      }
      const legacy = legacyKey ? await fieldDb.refdata.get(legacyKey) : null;
      if (!legacy || !legacy.options) return null;
      const { options, fields } = mergeRefDataOptions(legacy.options, null, []);
      await fieldDb.refdata.put({ key, options, fields, fetched_at: legacy.fetched_at ?? null });
      return { options, fields };
    });
  } catch (err) {
    console.error('fieldStore.readRefData failed:', err);
    throw err;
  }
}

/**
 * Merge a SUCCESSFUL fetch into the row under `key`: { key, options, fields,
 * fetched_at }. The base is that row, else the legacy row, so a Load hydrate
 * that runs before the first read cannot drop a legacy list the server
 * returned empty. Writes only when at least one field took a list, so an
 * empty row is never written over a populated one. Returns whether it wrote.
 *
 * fetched_at is stamped here as an ISO string. Nothing reads it.
 */
export async function mergeRefData(key, legacyKey, fetched, requestedFields) {
  try {
    await ensureOpen();
    return await fieldDb.transaction('rw', fieldDb.refdata, async () => {
      const base = (await fieldDb.refdata.get(key))
        || (legacyKey ? await fieldDb.refdata.get(legacyKey) : null);
      const { options, fields, replaced } = mergeRefDataOptions(base?.options, fetched, requestedFields);
      if (replaced.length === 0) return false;
      await fieldDb.refdata.put({ key, options, fields, fetched_at: new Date().toISOString() });
      return true;
    });
  } catch (err) {
    console.error('fieldStore.mergeRefData failed:', err);
    throw err;
  }
}

/** All cached site cards. */
export async function getSites() {
  try {
    await ensureOpen();
    return await fieldDb.sites.toArray();
  } catch (err) {
    console.error('fieldStore.getSites failed:', err);
    throw err;
  }
}

/** All cached records for one site. */
export async function getRecords(siteId) {
  try {
    await ensureOpen();
    return await fieldDb.records.where('site_id').equals(siteId).toArray();
  } catch (err) {
    console.error('fieldStore.getRecords failed:', err);
    throw err;
  }
}

/**
 * Is this row carrying unsent local work? (F-40 Phase 2)
 *
 * THE canonical dirty predicate. `dirty` is a SENTINEL SET (see the row
 * shape at the top of this file): values are never read, only key
 * presence. Exported so the live count in useFieldDrain and the
 * imperative count below share ONE definition rather than two drifting
 * copies — same reason buildBaseline was extracted and ACTIONABLE_STATUSES
 * is imported instead of re-compared inline.
 *
 * NOTE: FieldRecordsList re-states this predicate inline on purpose. That
 * component is presentation-only (React + CSS, zero db imports) and
 * importing this module would drag Dexie into a dumb grid. If the rule
 * changes, change it there too.
 */
export function isDirtyRecord(record) {
  return Object.keys((record && record.dirty) || {}).length > 0;
}

/**
 * How many cached records hold unsent edits (F-40 Phase 2).
 *
 * ⚠️ FULL-TABLE SCAN, AND DELIBERATELY SO. A rep can dirty records across
 * several sites in one shift, so whatever the shell happens to hold in
 * memory (one site) is not a sufficient basis — the honest number is
 * device-wide. `dirty` is a nested sentinel object, so no Dexie index can
 * serve this: key-presence inside an object is not indexable. A scan is
 * the only implementation available without a schema migration.
 *
 * Uses .filter().count() rather than .toArray() then filter: both scan
 * every row (the scan is the point), but this one never materialises an
 * array of full records — each carries baseline + working + child arrays +
 * media[]. That distinction is what keeps the scan survivable on a device
 * holding thousands of rows.
 *
 * @returns {Promise<number>}
 */
export async function countDirtyRecords() {
  try {
    await ensureOpen();
    return await fieldDb.records.filter(isDirtyRecord).count();
  } catch (err) {
    console.error("fieldStore.countDirtyRecords failed:", err);
    throw err;
  }
}

/** One record by its pk (Cosmos doc id) — NOT tool_id, which may repeat. */
export async function getRecord(recordId) {
  try {
    await ensureOpen();
    return await fieldDb.records.get(recordId);
  } catch (err) {
    console.error('fieldStore.getRecord failed:', err);
    throw err;
  }
}

/**
 * Every distinct record_id referenced by ANY syncQueue item, whatever its
 * status — pending, saved-not-yet-reconciled, conflict, error, not_found,
 * failed. A row a queue item points at still carries work (or a pending
 * review decision), so loadSiteRecords must never hide it. Items with a
 * falsy record_id are skipped.
 *
 * Reads fieldDb.syncQueue directly, as the rest of this file does — no
 * fieldOutbox import (that module imports this one).
 *
 * @returns {Promise<Set<string>>}
 */
export async function getQueuedRecordIds() {
  try {
    await ensureOpen();
    const items = await fieldDb.syncQueue.toArray();
    const ids = new Set();
    for (const item of items) {
      if (item && item.record_id) ids.add(item.record_id);
    }
    return ids;
  } catch (err) {
    console.error('fieldStore.getQueuedRecordIds failed:', err);
    throw err;
  }
}

/**
 * Delete the ENTIRE tmg-field database — the "Delete from device" hook.
 * The next fieldDb operation transparently re-creates it empty.
 */
export async function purgeFieldDB() {
  try {
    await fieldDb.delete();
  } catch (err) {
    console.error('fieldStore.purgeFieldDB failed:', err);
    throw err;
  }
}
